import React, {
	ReactElement,
	useCallback,
	useMemo,
	useState,
} from 'react';
import {
	Badge,
	Grid,
	Space,
} from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGasPump } from '@fortawesome/pro-light-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { PaperClipOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from 'react-query';
import {
	AttachmentTypes,
	Currencies,
	FuelTypes,
	HireInvoiceItemStates,
	VoyageBunkerTypes,
} from '@shared/utils/constants';
import { Values } from '@shared/utils/objectEnums';
import { VoyageBunkerEntry } from '@api/utils/sequelize/getVoyageBunkers';
import {
	addAttachment,
	createVoyageBunker,
	deleteAttachment,
	deleteVoyageBunker,
	updateVoyageBunker,
} from '@client/lib/api';
import EditableTable from '@client/components/EditableTable';
import Button from '@client/components/Button';
import {
	BunkerSide,
	getRows,
	getBunkerTableColumns,
	SingleBunkerRow,
	toPnlPrice,
} from '@client/components/getBunkerTableColumns';
import useAttachments from '@client/utils/hooks/useAttachments';
import showErrorNotification from '@client/utils/showErrorNotification';
import Card from '../Card/Card';
import style from './BunkersTable.module.css';

type BunkersTableProps = {
	voyageId: number;
	tcTermsId?: number | null;
	bunkers: VoyageBunkerEntry[];
	refreshDetails: () => void | Promise<void>;
	currency: Values<typeof Currencies>;
	variant?: 'bodBor' | 'single';
	singleType?: BunkerSide;
	availableFuelGrades?: Values<typeof FuelTypes>[];
	showOtherTable?: boolean;
	size?: 'middle' | 'small';
};

const copyQuantityToMin: Array<{
	field: keyof SingleBunkerRow;
	fn: (row: SingleBunkerRow, changed: Partial<SingleBunkerRow>) => number | null | undefined;
}> = [{
	field: 'minQuantity',
	fn: (_row, changed) => changed.quantity,
}];

const mapBodBorRow = (vb: VoyageBunkerEntry): SingleBunkerRow => ({
	key: String(vb.id),
	id: vb.id,
	type: vb.type,
	fuelGrade: vb.Bunker.fuelGrade,
	quantity: vb.Bunker.quantity ?? null,
	minQuantity: vb.Bunker?.minQuantity ?? null,
	maxQuantity: vb.Bunker?.maxQuantity ?? null,
	price: toPnlPrice(vb),
	estimated: vb.estimated ?? true,
	persistedEstimated: vb.estimated ?? true,
	state: vb.state ?? null,
	locked: (vb.state !== HireInvoiceItemStates.PENDING || vb.disableEdit),
	internalNote: vb.internalNote ?? null,
	attachments: vb.attachments ?? [],
});

const BunkersTable = ({
	voyageId,
	tcTermsId,
	bunkers,
	refreshDetails,
	currency,
	variant = 'bodBor',
	singleType,
	availableFuelGrades,
	showOtherTable = true,
	size = 'middle',
}: BunkersTableProps): ReactElement => {
	const screens = Grid.useBreakpoint();
	const queryClient = useQueryClient();
	const [mirroredRedeliveryRow, setMirroredRedeliveryRow] = useState<SingleBunkerRow>();
	const [attachmentsModalBunkerId, setAttachmentsModalBunkerId] = useState<number | null>(null);

	const attachmentsModalAttachments = useMemo(
		() => bunkers.find((b) => b.id === attachmentsModalBunkerId)?.attachments ?? [],
		[bunkers, attachmentsModalBunkerId],
	);

	const saveAttachmentsAndClose = async () => {
		const bunkerId = attachmentsModalBunkerId;
		setAttachmentsModalBunkerId(null);

		if (bunkerId == null) {
			return;
		}

		const keptIds = uploaded.map((file) => (file as File & { attachmentId?: number }).attachmentId);
		const added = uploaded.filter((_file, i) => keptIds[i] == null);
		const removed = attachmentsModalAttachments.filter((a) => !keptIds.includes(a.id));

		if (added.length === 0 && removed.length === 0) {
			return;
		}

		try {
			await Promise.all([
				...added.map((file) => addAttachment(AttachmentTypes.VOYAGE_BUNKER, bunkerId, file)),
				...removed.map((attachment) => deleteAttachment(attachment.id)),
			]);
		} catch (e) {
			showErrorNotification('Could not save attachments', e as Error);
		}

		await refreshDetails();
	};

	const {
		uploaded,
		modal: attachmentsModal,
	} = useAttachments({
		attachments: attachmentsModalAttachments,
		open: attachmentsModalBunkerId != null,
		setOpen: () => saveAttachmentsAndClose(),
		spacerWidth: '40%',
		attachmentTypeLabel: 'bunker',
	});

	const refreshItineraryQueries = useCallback(async () => {
		await Promise.all([
			queryClient.invalidateQueries(['itinerary', voyageId]),
			queryClient.invalidateQueries(['robs', voyageId]),
		]);
	}, [queryClient, voyageId]);

	const showInternalNote = variant === 'bodBor';

	const columns = useMemo(
		() => getBunkerTableColumns({
			variant: 'single',
			currency,
			fuelGrades: availableFuelGrades,
			showInternalNote,
		}),
		[currency, availableFuelGrades, showInternalNote],
	);

	const deliveryRows = useMemo(
		() => bunkers
			.filter((b) => b.type === VoyageBunkerTypes.DELIVERY)
			.map(mapBodBorRow),
		[bunkers],
	);

	const redeliveryRows = useMemo(
		() => bunkers
			.filter((b) => b.type === VoyageBunkerTypes.REDELIVERY)
			.map(mapBodBorRow),
		[bunkers],
	);

	const singleRows = useMemo(() => getRows(bunkers), [bunkers]);

	const otherRows = useMemo(
		() => getRows(
			bunkers.filter((b) => (
				b.type === VoyageBunkerTypes.OWNERS_ACCOUNT ||
				b.type === VoyageBunkerTypes.CHARTERERS_ACCOUNT
			)),
			{ includeNoteAndAttachments: showInternalNote },
		),
		[bunkers, showInternalNote],
	);

	const otherColumns = useMemo(
		() => getBunkerTableColumns({
			variant: 'other',
			currency,
			allowGradeEdit: true,
			fuelGrades: availableFuelGrades,
			showInternalNote,
		}),
		[currency, availableFuelGrades, showInternalNote],
	);

	const onAddNewDeliveryRow = useCallback((deliveryRow: any) => {
		if (deliveryRow) {
			setMirroredRedeliveryRow(deliveryRow);
		}
	}, []);

	const upsertBunkerMutation = useMutation(
		async ({
			id,
			type,
			payload,
		}: {
			id: number | null;
			type: Values<typeof VoyageBunkerTypes>;
			payload: {
				fuelGrade: Values<typeof FuelTypes>;
				quantity: number;
				minQuantity: number;
				maxQuantity: number | undefined;
				pricePerTon: number;
				currency: Values<typeof Currencies>;
				exchangeRate: number;
				estimated: boolean;
				internalNote?: string | null;
			};
		}) => {
			if (id != null) {
				await updateVoyageBunker(voyageId, id, { ...payload, type });
			} else {
				await createVoyageBunker(voyageId, { type, ...payload, tcTermsId });
			}
		},
	);

	const deleteBunkerMutation = useMutation(
		(bunkerId: number) => deleteVoyageBunker(voyageId, bunkerId),
		{
			onSuccess: async () => {
				await refreshDetails();
				await refreshItineraryQueries();
			},
		},
	);

	const upsertBunker = async (
		type: Values<typeof VoyageBunkerTypes>,
		id: number | null,
		fuelGrade: Values<typeof FuelTypes> | null,
		quantity: number | null,
		minQuantity: number | null,
		maxQuantity: number | null,
		price: number | null,
		estimated: boolean | null,
		internalNote?: string | null,
	) => {
		if (fuelGrade == null || quantity == null || price == null) {
			return;
		}

		await upsertBunkerMutation.mutateAsync({
			id,
			type,
			payload: {
				fuelGrade,
				quantity,
				minQuantity: minQuantity ?? quantity,
				maxQuantity: maxQuantity ?? undefined,
				pricePerTon: price,
				currency,
				exchangeRate: 1,
				estimated: estimated ?? true,
				...(showInternalNote ? { internalNote: internalNote ?? null } : {}),
			},
		});
	};

	const saveSingleRow = async (row: SingleBunkerRow, fixedType?: Values<typeof VoyageBunkerTypes>) => {
		const type = row.type ?? fixedType ?? null;

		if (type == null) {
			return;
		}

		await upsertBunker(
			type,
			row.id,
			row.fuelGrade,
			row.quantity,
			row.minQuantity,
			row.maxQuantity,
			row.price,
			row.estimated,
			row.internalNote,
		);

		await refreshDetails();
		await refreshItineraryQueries();
	};

	const saveDeliveryRow = async (row: SingleBunkerRow) => {
		await upsertBunker(
			VoyageBunkerTypes.DELIVERY,
			row.id,
			row.fuelGrade,
			row.quantity,
			row.minQuantity,
			row.maxQuantity,
			row.price,
			row.estimated,
			row.internalNote,
		);

		// Reset the mirror trigger, but keep the redelivery draft row on screen
		// (clearing addNewOverride is a no-op in EditableTable) so the user can
		// tweak and save the mirrored redelivery values after saving delivery.
		setMirroredRedeliveryRow(undefined);

		await refreshDetails();
		await refreshItineraryQueries();
	};

	const deleteSingleRow = async (row: SingleBunkerRow) => {
		if (row.id) {
			await deleteBunkerMutation.mutateAsync(row.id);
		}
	};

	const getAttachAction = useCallback((row: SingleBunkerRow) => {
		if (row.id == null) {
			return null;
		}

		const count = row.attachments?.length ?? 0;

		return (
			<Button
				type="link"
				onClick={() => setAttachmentsModalBunkerId(row.id as number)}
				icon={(
					<Badge
						count={count}
						size="small"
						offset={[4, -2]}
						overflowCount={9}
					>
						<PaperClipOutlined className={style.buttonIcon} />
					</Badge>
				)}
			/>
		);
	}, []);

	const cardTitle = (
		<Space>
			<FontAwesomeIcon color="red" icon={faGasPump as IconProp} />
			Bunkers
		</Space>
	);

	if (variant === 'single') {
		return (
			<Card slim title={cardTitle}>
				<EditableTable<SingleBunkerRow, 'key'>
					data-testid="bunkers-single-table"
					keyDataIndex="key"
					columns={columns as any}
					dataSource={singleRows}
					transformers={copyQuantityToMin}
					size="small"
					pagination={false}
					bordered
					iconButtons
					useCards={screens.xs}
					scroll={{ x: 'max-content' }}
					tableLayout="fixed"
					allowAddNew
					autoFocusOnAdd
					addNewText="Add bunker"
					addButtonTestId="add-bunker"
					enableDelete={(row) => !row.locked}
					onSave={(_key, row) => saveSingleRow(row, singleType)}
					onAddNew={(row) => saveSingleRow(row, singleType)}
					onDelete={(_key, row) => deleteSingleRow(row)}
					addNewContainerClassName={style.addNewButtonContainer}
				/>
			</Card>
		);
	}

	const rowsKey = (prefix: string, rows: SingleBunkerRow[]) => (
		`${prefix}-${rows.map((r) => r.id).join('-') || 'empty'}`
	);
	const deliveryTableKey = rowsKey('delivery', deliveryRows);
	const redeliveryTableKey = rowsKey('redelivery', redeliveryRows);
	const otherTableKey = rowsKey('other', otherRows);

	return (
		<>
			<Card title={cardTitle} slim>
				<EditableTable<SingleBunkerRow, 'key'>
					key={deliveryTableKey}
					data-testid="bunkers-delivery-table"
					keyDataIndex="key"
					columns={columns as any}
					dataSource={deliveryRows}
					transformers={copyQuantityToMin}
					size="small"
					pagination={false}
					bordered
					iconButtons
					useCards={screens.xs}
					scroll={{ x: 'max-content' }}
					tableLayout="fixed"
					allowAddNew
					autoFocusOnAdd
					addNewText="Add delivery bunker"
					addButtonTestId="add-delivery-bunker"
					onAddNewChange={(row) => {
						onAddNewDeliveryRow(row);
					}}
					enableDelete={(row) => !row.locked}
					getActionsBetweenEditAndDelete={getAttachAction}
					onSave={(_key, row) => saveDeliveryRow(row)}
					onAddNew={(row) => saveDeliveryRow(row)}
					onDelete={(_key, row) => deleteSingleRow(row)}
					addNewContainerClassName={style.addNewButtonContainer}
				/>
				<EditableTable<SingleBunkerRow, 'key'>
					key={redeliveryTableKey}
					data-testid="bunkers-redelivery-table"
					keyDataIndex="key"
					columns={columns as any}
					dataSource={redeliveryRows}
					transformers={copyQuantityToMin}
					size="small"
					pagination={false}
					bordered
					iconButtons
					useCards={screens.xs}
					scroll={{ x: 'max-content' }}
					tableLayout="fixed"
					autoFocusOnAdd
					addNewText="Add redelivery bunker"
					addButtonTestId="add-redelivery-bunker"
					addNewOverride={mirroredRedeliveryRow}
					allowAddNew
					enableDelete={(row) => !row.locked}
					getActionsBetweenEditAndDelete={getAttachAction}
					onSave={(_key, row) => saveSingleRow(row, VoyageBunkerTypes.REDELIVERY)}
					onAddNew={(row) => saveSingleRow(row, VoyageBunkerTypes.REDELIVERY)}
					onDelete={(_key, row) => deleteSingleRow(row)}
					addNewContainerClassName={style.addNewButtonContainer}
				/>
				{showOtherTable && (
					<div style={{ marginTop: 24 }}>
						<EditableTable<SingleBunkerRow, 'key'>
							key={otherTableKey}
							data-testid="bunkers-other-table"
							keyDataIndex="key"
							columns={otherColumns as any}
							dataSource={otherRows}
							transformers={copyQuantityToMin}
							size={size}
							pagination={false}
							bordered
							iconButtons
							useCards={screens.xs}
							scroll={{ x: 'max-content' }}
							tableLayout="fixed"
							allowAddNew
							autoFocusOnAdd
							addNewText="Add other bunker"
							addButtonTestId="add-other-bunker"
							disableEdit={(row) => row.state !== HireInvoiceItemStates.PENDING}
							enableDelete={(row) => row.id != null && !row.locked}
							getActionsBetweenEditAndDelete={getAttachAction}
							onSave={(_key, row) => saveSingleRow(row)}
							onAddNew={(row) => saveSingleRow(row)}
							onDelete={(_key, row) => deleteSingleRow(row)}
							addNewContainerClassName={style.addNewButtonContainer}
						/>
					</div>
				)}
			</Card>
			{attachmentsModal}
		</>
	);
};

export default BunkersTable;
